// =====================================================
// TYPES: CONTACT
// Description: Types TypeScript pour les contacts
// =====================================================

// Les statuts contact sont les mêmes que les statuts équipage + 'prospect'
// Le statut est synchronisé automatiquement avec le statut de l'équipage
export type ContactStatus =
  | 'prospect'
  | 'pre_registered'
  | 'registered'
  | 'client'
  // Cycle BED (migration 00178, cahier L18-23) — synchronisés depuis l'équipage
  | 'bed_downloaded'
  | 'bed_incomplete'
  | 'bed_incomplete_payment'
  | 'bed_complete'
  | 'cancellation_compliant'
  | 'cancellation_non_compliant'
  | 'postponement_compliant'
  | 'postponement_non_compliant'
  | 'completed'
  | 'archived';

// Raison d'invalidation d'un email (délivrabilité Mailjet, TICKET-CRM-13)
export type EmailInvalidReason = 'hard_bounce' | 'blocked' | 'spam';

export const EMAIL_INVALID_REASON_LABELS: Record<EmailInvalidReason, string> = {
  hard_bounce: 'adresse invalide',
  blocked: 'bloqué',
  spam: 'signalé en spam',
};

export interface Contact {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  birth_date: string | null;
  status: ContactStatus;
  origin_id: string | null;
  school_id: string | null;
  notes: string | null;
  assigned_to: string | null;
  // Adresse
  address: string | null;
  city: string | null;
  postal_code: string | null;
  country: string | null;
  // Statut étudiant
  is_not_student: boolean;
  socio_professional_category: string | null;
  // Délivrabilité email (TICKET-CRM-13, migration 00158) — NULL = adresse valide.
  // Posé par le webhook Mailjet (hard bounce/blocked/spam), reset si l'email change.
  email_invalid_at: string | null;
  email_invalid_reason: EmailInvalidReason | null;
  created_at: string;
  updated_at: string;
  // Nombre d'appels + date du dernier appel (calculés depuis timeline_events)
  call_count?: number;
  last_call_at?: string | null;
  // Relations (chargées optionnellement)
  origin?: {
    id: string;
    name: string;
    label: string;
  } | null;
  school?: {
    id: string;
    name: string;
  } | null;
  products?: {
    id: string;
    name: string;
  }[];
  assigned_user?: {
    id: string;
    email: string;
    full_name: string | null;
  } | null;
  newsletter_opt_in: boolean;
  // ✨ Sprint 5 : Équipages
  crew_numbers?: string[]; // Ex: ["0042", "0087"]
  is_in_crew?: boolean; // true si membre d'au moins un équipage
  // Retour 07/07 (E) : n° BPI du contact (via form_submissions, exposé sur
  // contacts_filter_view par la migration 00151). Ex: "BPI-2027-0042".
  bpi_code?: string | null;
}

export interface CreateContactDto {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  birth_date?: string;
  origin_id?: string;
  school_id?: string;
  notes?: string;
  product_ids?: string[];
  // Adresse
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  // Statut étudiant
  is_not_student?: boolean;
  socio_professional_category?: string;
}

export interface UpdateContactDto {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  birth_date?: string | null;
  status?: ContactStatus;
  origin_id?: string;
  school_id?: string | null;
  notes?: string;
  assigned_to?: string | null;
  product_ids?: string[];
  // Adresse
  address?: string | null;
  city?: string | null;
  postal_code?: string | null;
  country?: string | null;
  // Statut étudiant
  is_not_student?: boolean;
  socio_professional_category?: string | null;
  newsletter_opt_in?: boolean;
}

export const CONTACT_STATUSES: ContactStatus[] = [
  'prospect',
  'pre_registered',
  'registered',
  'client',
  'bed_downloaded',
  'bed_incomplete',
  'bed_incomplete_payment',
  'bed_complete',
  'cancellation_compliant',
  'cancellation_non_compliant',
  'postponement_compliant',
  'postponement_non_compliant',
  'completed',
  'archived',
];

export const CONTACT_STATUS_LABELS: Record<ContactStatus, string> = {
  prospect: 'Prospect',
  pre_registered: 'Pré-inscrit',
  registered: 'Inscrit',
  client: 'Client',
  bed_downloaded: 'BED téléchargé',
  bed_incomplete: 'BED incomplet — infos',
  bed_incomplete_payment: 'BED incomplet — paiement',
  bed_complete: 'BED complet',
  cancellation_compliant: 'Annulation conforme',
  cancellation_non_compliant: 'Annulation non conforme',
  postponement_compliant: 'Report conforme',
  postponement_non_compliant: 'Report non conforme',
  completed: 'Inscrit définitif',
  archived: 'Archivé',
};

export const CONTACT_STATUS_COLORS: Record<
  ContactStatus,
  { bg: string; text: string; border: string }
> = {
  prospect: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
  },
  pre_registered: {
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    border: 'border-yellow-200',
  },
  registered: {
    bg: 'bg-teal-50',
    text: 'text-teal-700',
    border: 'border-teal-200',
  },
  client: {
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    border: 'border-purple-200',
  },
  bed_downloaded: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
  },
  bed_incomplete: {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-200',
  },
  bed_incomplete_payment: {
    bg: 'bg-orange-100',
    text: 'text-orange-800',
    border: 'border-orange-300',
  },
  bed_complete: {
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-200',
  },
  cancellation_compliant: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
  },
  cancellation_non_compliant: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    border: 'border-red-300',
  },
  postponement_compliant: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
  },
  postponement_non_compliant: {
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    border: 'border-blue-300',
  },
  completed: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-300',
  },
  archived: {
    bg: 'bg-stone-100',
    text: 'text-stone-600',
    border: 'border-stone-300',
  },
};
